import { useEffect, useState } from "react";
import { Link, useNavigate, Navigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import SanaaMark from "@/components/SanaaMark";
import LangSwitcher from "@/components/LangSwitcher";
import ThemeToggle from "@/components/ThemeToggle";
import { LogOut, LayoutDashboard, ShieldCheck, Tag, Mail, ExternalLink, CheckCircle2, XCircle, AlertCircle } from "lucide-react";

export default function Admin() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [coupons, setCoupons] = useState(null);
  const [emailLog, setEmailLog] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user?.is_admin) return;
    Promise.all([
      api.get("/billing/admin/coupons"),
      api.get("/billing/admin/email-log"),
    ]).then(([c, e]) => {
      setCoupons(c.data);
      setEmailLog(e.data);
    }).finally(() => setLoading(false));
  }, [user]);

  if (!user) return null;
  if (!user.is_admin) return <Navigate to="/app" replace />;

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="sticky top-0 z-30 border-b border-border/60 bg-background/70 backdrop-blur-xl">
        <div className="mx-auto max-w-7xl px-5 py-4 flex items-center justify-between gap-3">
          <Link to="/" className="flex items-center gap-2">
            <SanaaMark size={26} />
            <span className="font-display tracking-tight">Sanaa AI</span>
          </Link>
          <div className="flex items-center gap-2">
            <button onClick={() => nav("/app")} data-testid="admin-back-suite"
              className="inline-flex h-9 items-center gap-2 rounded-sm border border-border/60 bg-card/40 hover:bg-card px-3 text-sm">
              <LayoutDashboard className="h-4 w-4" /> <span className="hidden sm:inline">Suite</span>
            </button>
            <LangSwitcher />
            <ThemeToggle />
            <button onClick={() => { logout(); nav("/"); }} data-testid="admin-logout" aria-label="Logout"
              className="h-9 w-9 inline-flex items-center justify-center rounded-sm border border-border/60 bg-card/40 hover:bg-card">
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-5 pt-12 pb-24">
        <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-3 inline-flex items-center gap-2">
          <ShieldCheck className="h-3 w-3" /> Admin
        </div>
        <h1 className="font-serif text-5xl sm:text-6xl tracking-tight leading-[1]">Operations.</h1>
        <p className="mt-4 text-muted-foreground text-lg max-w-2xl">
          Coupon redemptions, transactional email log. Add new coupons from the Stripe Dashboard — they appear here within seconds.
        </p>

        {/* Coupons */}
        <section className="mt-12">
          <h2 className="font-display text-xl mb-4 flex items-center gap-2"><Tag className="h-4 w-4" /> Promotion codes & coupons</h2>
          {loading && <div className="text-sm text-muted-foreground">Loading…</div>}
          {coupons && !coupons.available && (
            <div className="rounded-sm border border-dashed border-border/60 bg-card/40 p-6 flex items-start gap-3">
              <AlertCircle className="h-4 w-4 mt-0.5 text-muted-foreground" />
              <div className="text-sm text-muted-foreground">
                {coupons.message}{" "}
                <a href="https://dashboard.stripe.com/test/coupons" target="_blank" rel="noreferrer"
                   className="text-foreground underline-offset-2 hover:underline">
                  Open Stripe Dashboard <ExternalLink className="inline h-3 w-3" />
                </a>
              </div>
            </div>
          )}
          {coupons && coupons.available && (
            <>
              {coupons.promotion_codes?.length === 0 && coupons.coupons?.length === 0 ? (
                <div className="rounded-sm border border-dashed border-border/60 bg-card/40 p-6 text-sm text-muted-foreground">
                  No coupons created yet.{" "}
                  <a href="https://dashboard.stripe.com/coupons" target="_blank" rel="noreferrer"
                     className="text-foreground underline-offset-2 hover:underline">
                    Create one in Stripe <ExternalLink className="inline h-3 w-3" />
                  </a>
                </div>
              ) : (
                <div className="rounded-sm border border-border/60 overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-secondary/60">
                      <tr>
                        <Th>Code</Th>
                        <Th>Coupon</Th>
                        <Th>Discount</Th>
                        <Th>Redeemed</Th>
                        <Th>Status</Th>
                        <Th>Expires</Th>
                      </tr>
                    </thead>
                    <tbody>
                      {(coupons.promotion_codes || []).map((p) => {
                        const c = (coupons.coupons || []).find((cc) => cc.id === p.coupon_id);
                        const discount = c?.percent_off
                          ? `${c.percent_off}% off`
                          : c?.amount_off
                          ? `$${c.amount_off.toFixed(2)} off`
                          : "—";
                        return (
                          <tr key={p.id} className="border-t border-border/60" data-testid={`coupon-row-${p.id}`}>
                            <td className="px-4 py-2.5 font-mono text-xs">{p.code}</td>
                            <td className="px-4 py-2.5">{p.coupon_name || p.coupon_id}</td>
                            <td className="px-4 py-2.5">{discount}</td>
                            <td className="px-4 py-2.5 font-mono">
                              {p.times_redeemed}{p.max_redemptions ? ` / ${p.max_redemptions}` : ""}
                            </td>
                            <td className="px-4 py-2.5">
                              {p.active ? (
                                <span className="inline-flex items-center gap-1 text-xs" style={{ color: "#16a34a" }}>
                                  <CheckCircle2 className="h-3 w-3" /> Active
                                </span>
                              ) : (
                                <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                                  <XCircle className="h-3 w-3" /> Inactive
                                </span>
                              )}
                            </td>
                            <td className="px-4 py-2.5 text-muted-foreground">
                              {p.expires_at ? new Date(p.expires_at).toLocaleDateString() : "—"}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </section>

        {/* Email log */}
        <section className="mt-16">
          <h2 className="font-display text-xl mb-4 flex items-center gap-2"><Mail className="h-4 w-4" /> Transactional email log</h2>
          {emailLog.length === 0 ? (
            <div className="rounded-sm border border-dashed border-border/60 bg-card/40 p-6 text-sm text-muted-foreground">
              No transactional emails yet.
            </div>
          ) : (
            <div className="rounded-sm border border-border/60 overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-secondary/60">
                  <tr>
                    <Th>When</Th>
                    <Th>Recipient</Th>
                    <Th>Subject</Th>
                    <Th>Kind</Th>
                    <Th>Status</Th>
                  </tr>
                </thead>
                <tbody>
                  {emailLog.map((m) => (
                    <tr key={m.id} className="border-t border-border/60" data-testid={`email-row-${m.id}`}>
                      <td className="px-4 py-2.5 text-muted-foreground">{new Date(m.created_at).toLocaleString()}</td>
                      <td className="px-4 py-2.5 font-mono text-xs">{m.to}</td>
                      <td className="px-4 py-2.5">{m.subject}</td>
                      <td className="px-4 py-2.5 text-xs">
                        <span className="bg-secondary/60 px-2 py-0.5 rounded-sm font-mono">{m.kind}</span>
                      </td>
                      <td className="px-4 py-2.5">
                        <span
                          className="inline-block px-2 py-0.5 rounded-sm text-[11px] uppercase tracking-wider"
                          style={
                            m.status === "sent"
                              ? { background: "#16a34a22", color: "#16a34a" }
                              : m.status === "failed"
                              ? { background: "#ef444422", color: "#ef4444" }
                              : { background: "hsl(var(--muted))", color: "hsl(var(--muted-foreground))" }
                          }
                        >
                          {m.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <div className="mt-3 text-xs text-muted-foreground">
            Email delivery requires <code className="font-mono bg-secondary/60 px-1.5 py-0.5 rounded-sm">RESEND_API_KEY</code> +{" "}
            <code className="font-mono bg-secondary/60 px-1.5 py-0.5 rounded-sm">SENDER_EMAIL</code> in backend env.
            When unset, attempts are logged with status=<code className="font-mono">skipped_no_key</code>.
          </div>
        </section>
      </main>
    </div>
  );
}

function Th({ children }) {
  return (
    <th className="text-start font-medium px-4 py-2.5 text-xs uppercase tracking-[0.16em] text-muted-foreground">
      {children}
    </th>
  );
}
